import { Plus } from "lucide-react";

import { Avatar, AvatarFallback } from "@punto/ui/components/avatar";
import { cn } from "@punto/ui/lib/utils";

import { useUser } from "@/modules/auth/hooks/use-user";
import { User } from "@/modules/users/types";

/**
 * The "nobody holds this" affordance. By default it hides until the row it sits
 * in is hovered, the menu is open, or something inside that row takes focus —
 * which is right for the procedures TABLE, where the row is the hover target and
 * a page of them would otherwise be a wall of `+`.
 *
 * `group-focus-within/row` is the keyboard half of that reveal — `group-hover`
 * ignores focus, so without it a keyboard reader gets the button's focus ring
 * around an empty circle. Same pairing `document-delete-button` makes with
 * `has-[button:focus]`.
 *
 * `alwaysVisible` opts out of the whole reveal, for a host that is not a row:
 * a conversation header has nothing to hover, so a hidden affordance is one a
 * reader never discovers. Opt-in rather than the default, so the table keeps the
 * behaviour it was designed with.
 */
// The empty circle has to measure exactly what the filled one next to it does,
// so it takes the same `size` as `UserAvatar` rather than hardcoding one. The
// values mirror `avatarVariants` in @punto/ui — sm 24, md 32, lg 40 — because
// this is a plain div, not an `Avatar`: it renders a `+`, not initials, and has
// its own reveal-on-hover behaviour. The default stays `md` so the procedures
// table, which pairs it with an equally default `UserAvatar`, does not move.
const NOT_FOUND_SIZES = {
  sm: { circle: "h-6 w-6", icon: "size-3" },
  md: { circle: "h-8 w-8", icon: "size-4" },
  lg: { circle: "h-10 w-10", icon: "size-5" },
} as const;

export const UserNotFoundAvatar = ({
  open,
  alwaysVisible = false,
  size = "md",
  ...props
}: {
  open: boolean;
  alwaysVisible?: boolean;
  size?: "sm" | "md" | "lg";
}) => {
  const { circle, icon } = NOT_FOUND_SIZES[size];

  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full bg-muted text-muted-foreground",
        circle,
        !alwaysVisible &&
          "invisible group-focus-within/row:visible group-hover/row:visible",
        open && "visible",
      )}
      {...props}
    >
      <Plus className={icon} />
    </div>
  );
};

export const UserAvatar = ({
  user,
  className,
  size = "md",
  ...props
}: {
  /** Only `id` (the "is it me?" check) and `initials` (the fallback content) are read. */
  user?: Pick<User, "id" | "initials"> | null;
  className?: string;
  size?: "sm" | "md" | "lg";
}) => {
  const { user: me } = useUser();

  if (!user) {
    return null;
  }

  const itsMe = user.id === me?.id;

  return (
    <Avatar size={size} className={cn(className)} {...props}>
      <AvatarFallback variant={itsMe ? "me" : "default"}>
        {user?.initials}
      </AvatarFallback>
    </Avatar>
  );
};
